/**
 * routeToolResultToPin — pure decision: does a tool result deserve the voice
 * call's pinned-card slot (design turn 11 part B)?
 *
 * Deliberately CALLS tool-result-mapper's mapPersistedResultToMessage rather
 * than re-deriving the payload-shape/empty-result rules — the pinned card and
 * the eventual chat-history card must agree on what counts as "renderable"
 * (same empty-course-list / empty-refs skip conditions), so there is exactly
 * one place that decision lives. This function only translates that shared
 * Message.result shape into the narrower PinnedCard the voice UI renders,
 * plus two pin-specific rules that don't belong in the shared mapper:
 *   - courses: sort soonest-first and cap at MAX_PINNED_COURSES — the pinned
 *     carousel is a glance surface, not the full result list (the chat's
 *     CoursePanel still renders every course; only the pin's carousel is
 *     bounded here). ALL titles render: multi-location searches without a
 *     course_title legitimately merge mixed course types (FB33), and each
 *     card carries its own title — filtering to the first card's title hid
 *     every other program (and with it, whole cities) from the pin.
 *   - media: cap at MAX_PINNED_MEDIA_REFS — the pinned slot is a glance
 *     surface, not the full RAG source list.
 *
 * Pure (no React, no DOM) so it is trivially unit-testable.
 */

import { ComponentType, ToolName } from '@/types/chat';
import type { Course } from '@/types/course';
import type { RAGReference } from '@/types/rag';
import { sortSoonest } from './course-grouping';
import { getYouTubeId } from './youtube';
import { mapPersistedResultToMessage } from './tool-result-mapper';

export type PinnedCard =
  | { kind: 'courses'; courses: Course[] }
  | { kind: 'media'; refs: RAGReference[] };

/** The pinned media surface never shows more than this many refs. */
const MAX_PINNED_MEDIA_REFS = 5;

/** The pinned course carousel never shows more than this many courses — an
 *  unusually large same-title result set (no cap existed here before) would
 *  otherwise feed an unbounded carousel into the mobile pin. Bounds the
 *  PINNED carousel only; the full list still renders in the chat's
 *  CoursePanel. */
const MAX_PINNED_COURSES = 5;

/** A stable placeholder id — routeToolResultToPin only reads back
 *  mapPersistedResultToMessage's `result`, never its `id`. */
const PIN_PROBE_ID = 'pin-probe';

export function routeToolResultToPin(
  toolName: string,
  payload: unknown
): PinnedCard | null {
  if (toolName === ToolName.FIND_COURSES) {
    // Mirror useMessageManagement's insertToolResult unwrap: MCP normally
    // returns CourseCards ({ data: CourseCard[] }), but some SDK paths
    // deliver the bare array instead.
    const result = payload as { data?: unknown };
    const courses = Array.isArray(result?.data)
      ? (result.data as Course[])
      : Array.isArray(payload)
        ? (payload as Course[])
        : undefined;
    if (!courses) return null;

    const msg = mapPersistedResultToMessage(
      ComponentType.COURSE_BROWSER,
      courses,
      PIN_PROBE_ID
    );
    if (!msg?.result || msg.result.kind !== ComponentType.COURSE_BROWSER) {
      return null;
    }

    const ordered = sortSoonest(msg.result.courses);
    if (ordered.length === 0) return null;
    return {
      kind: 'courses',
      courses: ordered.slice(0, MAX_PINNED_COURSES),
    };
  }

  if (
    toolName === ToolName.BROWSE_AOL_CONTENT ||
    toolName === ToolName.FETCH_QUICK_AOL_CONTENT ||
    // Unified retrieval tool (P3): same translated RAGData envelope as
    // browse (see useMessageManagement's routing comment), so it pins the
    // same media card. P3.4 added the name to the chat-card sites but missed
    // this voice-mode router — a mid-call video search produced NO pinned
    // card while the chat card appeared after the call (prod report
    // 2026-07-28, mobile voice mode).
    toolName === ToolName.SEARCH_AOL_CONTENT
  ) {
    const msg = mapPersistedResultToMessage(
      ComponentType.RAG_CONTENT,
      payload,
      PIN_PROBE_ID
    );
    if (!msg?.result || msg.result.kind !== ComponentType.RAG_CONTENT) {
      return null;
    }
    const refs = [...(msg.result.ragContent.references?.references ?? [])].sort(
      (a, b) => (b.score ?? 0) - (a.score ?? 0)
    );
    if (refs.length === 0) return null;
    // Videos lead the stack (owner decision; matches RelatedMediaPanel's
    // video-first default): the first — default-visible — pinned card should
    // be the watchable one when a mixed result arrives mid-call. Within each
    // partition the score order is preserved.
    const videos = refs.filter((r) => getYouTubeId(r.url) !== null);
    const articles = refs.filter((r) => getYouTubeId(r.url) === null);
    let ordered = [...videos, ...articles];
    // The summarizer nominates one citation as the "hero" (the top pick) via
    // a `hero_citation` URL the backend puts alongside `summary`/`references`
    // on the RAG payload (it isn't inside the typed RAGData, so read it off
    // the raw payload). When present, lead the carousel with it so the
    // default-visible pinned card IS the top result — overriding the
    // video-first default for that one card only (owner decision: lead card,
    // no separate badge). The fast, no-summarizer path defaults hero to the
    // highest-scoring ref, so this is a no-op reorder there.
    const heroUrl =
      (payload as { hero_citation?: string | null } | null)?.hero_citation ??
      null;
    if (heroUrl) {
      const heroIdx = ordered.findIndex((r) => r.url === heroUrl);
      if (heroIdx > 0) {
        const [hero] = ordered.splice(heroIdx, 1);
        ordered = [hero, ...ordered];
      }
    }
    return {
      kind: 'media',
      refs: ordered.slice(0, MAX_PINNED_MEDIA_REFS),
    };
  }

  return null;
}
